import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import * as XLSX from 'xlsx';
import './Report.css';

interface BarLogData {
  id: number;
  created_at: string;
  user_username: string;
  customer_name: string | null;
  reservation_code: string;
  is_accepted: number;
  slot_name: string;
  discount_type: string;
  base_price: number;
  discount_percentage: number;
  final_price: number;
}

const Report: React.FC = () => {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [barLogs, setBarLogs] = useState<BarLogData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Set default dates to today
  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayString = `${year}-${month}-${day}`;

    setDateFrom(todayString);
    setDateTo(todayString);
  }, []);

  const handleSearch = async () => {
    if (!dateFrom) {
      setError('Data inizio è obbligatoria');
      return;
    }

    // Calculate date range in days
    const startDate = new Date(dateFrom);
    const endDate = dateTo ? new Date(dateTo) : new Date(dateFrom);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Check if date range exceeds 15 days
    if (diffDays > 15) {
      setError('Il periodo selezionato non può superare 15 giorni');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Format dates with times: start date at 00:00:00, end date at 23:59:59
      const dateFromWithTime = `${dateFrom} 00:00:00`;
      const dateToWithTime = dateTo ? `${dateTo} 23:59:59` : `${dateFrom} 23:59:59`;

      const response = await apiService.getBarLogs({
        date_from: dateFromWithTime,
        date_to: dateToWithTime,
      });

      if (response.code === 200 && response.data) {
        // Lambda returns data as { logs: [...], total: number }
        const logs = response.data.logs || response.data;
        setBarLogs(Array.isArray(logs) ? logs : []);
      } else {
        setError(response.message || 'Errore durante il caricamento dei dati');
      }
    } catch (err: any) {
      setError(err.message || 'Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    apiService.logout();
    window.location.reload();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCustomerTypeLabel = (discountType: string) => {
    switch (discountType) {
      case 'inps':
        return 'INPS';
      case 'oneyear':
        return 'Annuale';
      case 'sixmonths':
        return 'Semestrale';
      default:
        return discountType;
    }
  };

  const handleExportExcel = () => {
    // Prepare data for Excel
    const excelData = barLogs.map((log) => ({
      ID: log.id,
      Data: formatDate(log.created_at),
      Operatore: log.user_username || '-',
      Cliente: log.customer_name || '-',
      Prenotazione: log.reservation_code,
      Pasto: log.slot_name || '-',
      'Tipo Cliente': log.discount_type ? getCustomerTypeLabel(log.discount_type) : '-',
      'Prezzo Base': log.base_price != null ? `€${Number(log.base_price).toFixed(2)}` : '-',
      'Sconto %': log.discount_percentage != null ? `${log.discount_percentage}%` : '-',
      'Prezzo Finale': log.final_price != null ? `€${Number(log.final_price).toFixed(2)}` : '-',
      Accettato: log.is_accepted ? 'Sì' : 'No',
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Food Logs');

    // Generate filename with current date and time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const filename = `food_log_${year}${month}${day}_${hours}${minutes}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div className="report-container">
      <div className="report-header">
        <div className="logo-section">
          <div className="logo-brand-container">
            <img src="/images/logo_no_payoff.svg" alt="Logo" className="logo" />
            <span className="brand-text"><i>Food Report</i></span>
          </div>
        </div>
        <div className="header-buttons">
          <button className="back-btn" onClick={() => navigate('/')}>
            Indietro
          </button>
          <button className="logout-btn" onClick={logout}>
            Logout
          </button>
        </div>
      </div>

      <div className="report-content">
        <div className="filters-card">
          <h3>Filtri</h3>
          <div className="filters-form">
            <div className="form-group">
              <label htmlFor="date_from">Data inizio</label>
              <input
                id="date_from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label htmlFor="date_to">Data fine</label>
              <input
                id="date_to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <button
                onClick={handleSearch}
                disabled={loading}
                className="search-button"
              >
                {loading ? 'Caricamento...' : 'Cerca'}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {barLogs.length > 0 && (
          <div className="table-card">
            <div className="table-header">
              <h3>Risultati ({barLogs.length})</h3>
              <button onClick={handleExportExcel} className="export-button">
                Esporta Excel
              </button>
            </div>
            <div className="table-responsive">
              <table className="bar-logs-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Data</th>
                    <th>Operatore</th>
                    <th>Cliente</th>
                    <th>Prenotazione</th>
                    <th>Pasto</th>
                    <th>Tipo Cliente</th>
                    <th>Prezzo Finale</th>
                    <th>Accettato</th>
                  </tr>
                </thead>
                <tbody>
                  {barLogs.map((log) => (
                    <tr key={log.id}>
                      <td>{log.id}</td>
                      <td>{formatDate(log.created_at)}</td>
                      <td>{log.user_username || '-'}</td>
                      <td>{log.customer_name || '-'}</td>
                      <td>{log.reservation_code}</td>
                      <td>{log.slot_name || '-'}</td>
                      <td>{log.discount_type ? getCustomerTypeLabel(log.discount_type) : '-'}</td>
                      <td>{log.final_price != null ? `€${Number(log.final_price).toFixed(2)}` : '-'}</td>
                      <td>
                        <span className={`status-badge ${log.is_accepted ? 'accepted' : 'rejected'}`}>
                          {log.is_accepted ? 'Sì' : 'No'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && barLogs.length === 0 && dateFrom && (
          <div className="no-results">
            <p>Nessun risultato trovato</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Report;
